import { LoadingState } from "@/components/podkit/loading/LoadingState";
import { parseAsUserSettingsTab, USER_SETTINGS_MODAL_QUERY_PARAM } from "@/components/settings/user-settings-constants";
import { Flags } from "@/feature-flags";
import { useBootInAppChat } from "@/hooks/use-boot-in-app-chat";
import { useFlag } from "@/hooks/use-flag";

import { useLocalRunner } from "@/hooks/use-local-runner";
import { useOnboardingCompletion } from "@/hooks/use-onboarding-completion";
import { useOpenSettingsShortcut } from "@/hooks/use-open-settings-shortcut";
import { useSegmentIdentify } from "@/hooks/use-segment";
import { getPrincipal, onDidChangePrincipal } from "@/principal";
import { useGetAccount } from "@/queries/account-queries";
import { useListUserResourcePermissions } from "@/queries/group-queries";
import { useAuthenticatedUser } from "@/queries/user-queries";
import { useQueryClient } from "@tanstack/react-query";
import { LocalRunnerStatus } from "frontend-shared/local-runner";
import { useQueryState } from "nuqs";
import { useEffect, useMemo, useState, type FC } from "react";
import { Navigate, Outlet, useLocation, useParams } from "react-router-dom";

/**
 * This is a list of paths that we should not redirect to onboarding for.
 * If current path is in this list (or starts with one of these paths), we will not redirect to onboarding.
 */
const ONBOARDING_REDIRECT_IGNORE_LIST = ["/settings/profile", "/account-deleted"] as const;

export const AuthenticatedUserLayout: FC<{ LoadingComponent?: FC }> = ({ LoadingComponent = LoadingState }) => {
    const localRunner = useLocalRunner();
    useBootInAppChat();

    useSegmentIdentify();

    const [, setUserSettingsTab] = useQueryState(USER_SETTINGS_MODAL_QUERY_PARAM, parseAsUserSettingsTab);
    useOpenSettingsShortcut(() => {
        void setUserSettingsTab("profile");
    });

    // This hook loads the account, which is a dependency to most of the other queries
    const { data: account, isPending: isLoadingAccount, error: errorAccount } = useGetAccount();
    const { data: user, isPending: isLoadingUser } = useAuthenticatedUser();

    // Prefetch user permissions early so they're cached and ready when features need them.
    // When the permission dataloader flag is enabled, per-resource loading replaces this – see src/hooks/use-resource-permission.ts
    const { value: permDataloaderEnabled, loading: permFlagLoading } = useFlag(
        Flags.PermissionDataloaderEnabled,
        false,
    );
    useListUserResourcePermissions({ enabled: !permFlagLoading && !permDataloaderEnabled });
    const {
        isPendingOnboarding: isPendingOnaOnboarding,
        isLoadingOnboardingCompletion: isLoadingOnaOnboardingCompletion,
    } = useOnboardingCompletion();

    const debugNoOnboardingRedirect = useMemo(
        () => import.meta.env.DEV && localStorage.getItem("debug_no_onboarding_redirect") === "true",
        [],
    );

    const location = useLocation();
    const { inviteId } = useParams();

    // Forcibly re-render the component when the principal changes
    const queryClient = useQueryClient();
    const [, setPrincipalState] = useState<string | null>(null);
    useEffect(() => {
        return onDidChangePrincipal(() => {
            setPrincipalState(getPrincipal());
            queryClient.clear();
        });
    }, [queryClient]);

    // Wait for the account to be loaded
    if (!account && isLoadingAccount && localRunner.loading) {
        return <LoadingComponent />;
    }

    const needsRunnerScreen = LocalRunnerStatus.needsRunnerScreen(localRunner.status);
    const errorDismissed = localRunner.isRunnerErrorDismissed;

    if (needsRunnerScreen && !errorDismissed) {
        return <Navigate to="/local-runner" state={{ from: location }} replace />;
    }

    const isAccoundWithoutMemberships = account && (account.memberships || []).length === 0;

    if (isLoadingAccount) {
        return <LoadingComponent />;
    }

    // Redirect to login if the user is not authenticated - maintain the current location to redirect to after login
    if (!account && !errorAccount) {
        let to = "/login?";
        if (inviteId) {
            to += "inviteId=" + inviteId + "&";
        }
        to += "navigateTo=" + encodeURIComponent(location.pathname + location.search + location.hash);
        return <Navigate to={to} state={{ from: location }} replace />;
    }

    // Wait for the user to be loaded
    // but only if the account actually has memberships
    if ((!isAccoundWithoutMemberships && !user && isLoadingUser) || isLoadingOnaOnboardingCompletion) {
        return <LoadingComponent />;
    }

    if (
        ((isPendingOnaOnboarding && !debugNoOnboardingRedirect) || isAccoundWithoutMemberships) &&
        !isOnaOnboardingPath(location.pathname) &&
        !isOnboardingIgnoredPath(location.pathname, location.search) &&
        // this flag is set when the user skips onboarding
        // at the time of skipping, the onboarding completion flag may still be in the process of being set
        // so we use this flag to avoid putting the user back into the onboarding flow again after they skip
        !location.state?.isOnboardingSkipped
    ) {
        return (
            <Navigate to="/onboarding/ona/" state={{ from: location, organizationId: user?.organizationId }} replace />
        );
    }

    // If we got here, there's a user and we can render the layout
    return (
        <>
            <Outlet />
        </>
    );
};

function isOnaOnboardingPath(locationPath: string) {
    return locationPath?.startsWith("/onboarding/ona");
}

function isOnboardingIgnoredPath(locationPath: string, locationSearch?: string) {
    if (ONBOARDING_REDIRECT_IGNORE_LIST.some((path) => locationPath?.startsWith(path))) {
        return true;
    }
    // The user settings modal (opened via ?user-settings=...) should also bypass onboarding,
    // since it contains the delete account flow.
    if (locationSearch) {
        const params = new URLSearchParams(locationSearch);
        if (params.has(USER_SETTINGS_MODAL_QUERY_PARAM)) {
            return true;
        }
    }
    return false;
}
